import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaShoppingCart, FaHeart } from "react-icons/fa";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../auth/AuthContext";
import { formatPrice } from "../utils/currency";
import { getPartDetailPath } from "../utils/partDetailRoutes";
import { accountSettingsService } from "../services/apiService";

/**
 * Screen-2 style result card (OEM | Aftermarket | Used columns).
 * Compact horizontal layout so cards read well in narrow compare columns.
 */
const CatalogueComparePartCard = ({
  product,
  categoryData = null,
  columnKey = "aftermarket",
}) => {
  const [imgError, setImgError] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState("");
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const id = product.id || product._id;
  const to = getPartDetailPath(product, { categorySlug: categoryData?.slug });
  const discountPct =
    product.mrp && product.price && product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : null;
  const inStock = (product.stock ?? 0) > 0;
  const isUsed = String(product.condition || "").toLowerCase() === "used";
  const usedGrade = String(product.usedGrade || "").toUpperCase();
  const oemLine = product.partNumber || product.sku || "—";
  const showImg = product.image && !imgError;
  const rating = Number(product.rating ?? 0);
  const filledStars = Math.max(0, Math.min(5, Math.round(rating)));

  const borderLeft =
    columnKey === "new"
      ? "border-l-[3px] border-l-blue-500"
      : columnKey === "used"
        ? "border-l-[3px] border-l-violet-400"
        : "border-l-[3px] border-l-amber-500";

  const gradeLabel =
    usedGrade === "A"
      ? "Excellent"
      : usedGrade === "B"
        ? "Good"
        : usedGrade === "C"
          ? "Fair"
          : isUsed
            ? "Used"
            : null;

  const gradeClass =
    usedGrade === "A"
      ? "bg-emerald-500/15 text-emerald-400"
      : usedGrade === "B"
        ? "bg-amber-500/15 text-amber-300"
        : "bg-rose-500/12 text-rose-400";

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    try {
      await addToCart(
        {
          id,
          name: product.name,
          brand: product.brand,
          price: product.mrp || product.price,
          discountPrice:
            product.mrp && product.mrp > product.price ? product.price : null,
          imageUrl: product.image,
          partNumber: product.partNumber || product.sku,
          quantity: 1,
        },
        false
      );
      toast.success(`${product.name} added to cart!`);
    } catch {
      toast.error("Failed to add to cart");
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (wishlistBusy) return;
    setWishlistBusy(true);
    try {
      if (wishlistItemId) {
        await accountSettingsService.removeFromWishlist(wishlistItemId);
        setWishlistItemId("");
        toast.success("Removed from wishlist");
        return;
      }
      const rawProductId = String(product._id || product.id || "");
      const saved = await accountSettingsService.addToWishlist({
        partName: product.name || product.title || "Part",
        listingId: String(product.listingId || product.id || product._id || product.sku || ""),
        productId: /^[a-f\d]{24}$/i.test(rawProductId) ? rawProductId : undefined,
        partNumber: product.partNumber || product.sku,
        source: isUsed ? "used" : "new",
        price: product.price,
        notifyWhenAvailable: false,
      });
      setWishlistItemId(saved?.id || "");
      toast.success("Added to wishlist");
    } catch (error) {
      toast.error(error.message || "Could not update wishlist");
    } finally {
      setWishlistBusy(false);
    }
  };

  return (
    <article
      className={`border-b border-slate-700/70 px-3 py-3 transition-colors hover:bg-white/[0.03] last:border-b-0 ${borderLeft}`}
    >
      <div className="flex gap-3 min-w-0">
        <Link
          to={to}
          state={{
            product: { ...product, id, image: product.image },
            category: categoryData || { name: product.category || "Parts" },
          }}
          className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-700/80 bg-slate-950"
        >
          {showImg ? (
            <img
              loading="lazy"
              src={product.image}
              alt=""
              className="h-full w-full object-contain p-1"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="text-xl text-slate-600" aria-hidden>
              🔧
            </span>
          )}
        </Link>

        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          <Link
            to={to}
            state={{
              product: { ...product, id, image: product.image },
              category: categoryData || { name: product.category || "Parts" },
            }}
            className="min-w-0 block"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">
              {product.brand || "Brand"}
            </p>
            <h3 className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug text-slate-100">
              {product.name}
            </h3>
            <p className="mt-0.5 truncate font-mono text-[10px] text-slate-500">
              OEM: {oemLine}
            </p>
          </Link>

          <div className="flex flex-wrap items-center gap-1.5">
            {inStock && (
              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">
                In Stock
              </span>
            )}
            {isUsed ? (
              <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold text-violet-300">
                Used
              </span>
            ) : product.isOEM || product.origin === "OEM" ? (
              <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-bold text-sky-400">
                OEM Fit
              </span>
            ) : (
              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold text-amber-300">
                1yr Warranty
              </span>
            )}
            {isUsed && gradeLabel ? (
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${gradeClass}`}>
                {gradeLabel}
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={i < filledStars ? "text-amber-400" : "text-slate-700"}
                  >
                    ★
                  </span>
                ))}
                {rating > 0 && (
                  <span className="ml-0.5 tabular-nums">{rating.toFixed(1)}</span>
                )}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-2 pt-0.5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-1.5">
                <span className="text-lg font-bold tabular-nums text-amber-300">
                  {formatPrice(product.price || 0)}
                </span>
                {product.mrp && product.mrp > product.price && (
                  <>
                    <span className="text-[11px] text-slate-500 line-through tabular-nums">
                      {formatPrice(product.mrp)}
                    </span>
                    {discountPct != null && (
                      <span className="rounded bg-emerald-500/12 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">
                        {discountPct}% off
                      </span>
                    )}
                  </>
                )}
              </div>
              <p className="mt-0.5 text-[10px] text-slate-500">
                📦 Delivery by{" "}
                <b className="font-medium text-slate-300">Tomorrow, 8pm</b>
              </p>
              {isUsed && product.soldBy && (
                <p className="mt-0.5 text-[10px] text-slate-500">🏪 {product.soldBy}</p>
              )}
            </div>

            <div className="flex shrink-0 gap-1.5">
              <button
                type="button"
                onClick={handleAddToCart}
                className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-bold transition-opacity hover:opacity-90 ${
                  isUsed
                    ? "bg-gradient-to-br from-violet-800 to-violet-400 text-white"
                    : "bg-gradient-to-br from-amber-700 to-amber-400 text-black"
                }`}
              >
                <FaShoppingCart className="text-[10px]" />
                Add to Cart
              </button>
              <button
                type="button"
                onClick={handleToggleWishlist}
                disabled={wishlistBusy}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  wishlistItemId
                    ? "border-rose-500 bg-rose-500/15 text-rose-400"
                    : "border-slate-600 bg-slate-900/60 text-slate-400 hover:border-rose-400 hover:text-rose-400"
                }`}
                aria-label={wishlistItemId ? "Remove from wishlist" : "Add to wishlist"}
              >
                <FaHeart className="text-[12px]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default CatalogueComparePartCard;
